/**
 * Weltbank Climate Change Knowledge Portal, CMIP6 0.25° (ODbL; Modelldaten
 * CMIP6 CC BY-SA 4.0). Fertige Kennzahländerungen je Zelle gegenüber
 * 1995–2014 als Ensemble-Median, P10 und P90 über 27 Modelle.
 *
 * Direktabruf aus dem Browser: je Datei Dateikopf (≤ 16 KB, zwischengespeichert)
 * und danach 4 Byte für den Wert der Zelle. Die Referenzdateien der Quelle
 * liegen ausser für die Jahresmitteltemperatur als NetCDF-4/HDF5 vor und sind
 * für Bereichsabrufe nicht geeignet; die Referenz der Quelle wird deshalb nur
 * für die Jahresmitteltemperatur gezeigt.
 */
import { readNc3Axis, readNc3Header, readNc3Value, fetchRange, type Nc3Header, type RangeFetcher } from './nc3';
import { distanceKm, type FutureFileRecord, type FutureIndicator, type FutureLevel, type FutureRow, type Robustness, type Spread } from './types';

export const CCKP_BASE = 'https://wbg-cckp.s3.us-west-2.amazonaws.com/data/cmip6-x0.25';
export const CCKP_LICENCE =
  'World Bank Climate Change Knowledge Portal, CMIP6 0.25° (ODbL 1.0); CMIP6-Modelldaten CC BY-SA 4.0; Verfahren NEX-GDDP-CMIP6 (Thrasher et al. 2022)';
export const CCKP_REFERENCE = '1995–2014';
export const FILL_LIMIT = 1e19;

export type CckpStat = 'median' | 'p10' | 'p90';
export const CCKP_STATS: CckpStat[] = ['median', 'p10', 'p90'];

export interface CckpSelection extends FutureLevel {
  ssp: 'ssp245' | 'ssp585';
  period: string;
}

/** Näherung der globalen Erwärmung gegenüber 1850–1900 nach IPCC AR6 WG I, Tabelle SPM.1 (beste Schätzung). */
export const CCKP_SELECTIONS: CckpSelection[] = [
  { key: 'ssp245_2020-2039', ssp: 'ssp245', period: '2020-2039', label: 'SSP2-4.5 · 2020–2039', gwl: 1.5, gwlApprox: true },
  { key: 'ssp245_2040-2059', ssp: 'ssp245', period: '2040-2059', label: 'SSP2-4.5 · 2040–2059', gwl: 2.0, gwlApprox: true },
  { key: 'ssp245_2080-2099', ssp: 'ssp245', period: '2080-2099', label: 'SSP2-4.5 · 2080–2099', gwl: 2.7, gwlApprox: true },
  { key: 'ssp585_2080-2099', ssp: 'ssp585', period: '2080-2099', label: 'SSP5-8.5 · 2080–2099', gwl: 4.4, gwlApprox: true },
];

export interface CckpIndicator {
  code: string;
  indicator: FutureIndicator;
  definition: string;
  unit: string;
  /** Umrechnung in App-Einheiten (°F·d → K·d) */
  factor: number;
  /** Abweichung zur App-Definition, falls vorhanden */
  differs?: string;
}

/** Definitionen aus den Dateiköpfen (Attribut «calculation»), geprüft am 27.09.2026. */
export const CCKP_INDICATORS: CckpIndicator[] = [
  { code: 'hdd65', indicator: 'hdd', definition: 'Summe (18.3 °C − Tagesmittel), wenn positiv (65 °F)', unit: 'K·d', factor: 5 / 9, differs: 'Basis 18.3 °C statt der eingestellten Basis' },
  { code: 'cdd22', indicator: 'cdd', definition: 'Summe (Tagesmittel − 22 °C), wenn positiv', unit: 'K·d', factor: 1 },
  { code: 'hd30', indicator: 'hotDays', definition: 'Tagesmaximum ≥ 30 °C', unit: 'Tage', factor: 1 },
  { code: 'hd35', indicator: 'veryHotDays', definition: 'Tagesmaximum ≥ 35 °C', unit: 'Tage', factor: 1 },
  { code: 'tr', indicator: 'tropicalNights', definition: 'Tagesminimum > 20 °C', unit: 'Tage', factor: 1, differs: 'Quelle zählt > 20 °C, die App ≥ 20 °C' },
  { code: 'txx', indicator: 'txx', definition: 'höchstes Tagesmaximum des Jahres, Mittel der Periode', unit: '°C', factor: 1 },
  { code: 'tas', indicator: 'tMean', definition: 'Mittel der Tagesmittel', unit: '°C', factor: 1 },
];

export function anomalyUrl(code: string, sel: CckpSelection, stat: CckpStat): string {
  return `${CCKP_BASE}/${code}/ensemble-all-${sel.ssp}/anomaly-${code}-annual-mean_cmip6-x0.25_ensemble-all-${sel.ssp}_climatology_${stat}_${sel.period}.nc`;
}

export function historicalUrl(code: string): string {
  return `${CCKP_BASE}/${code}/ensemble-all-historical/climatology-${code}-annual-mean_cmip6-x0.25_ensemble-all-historical_climatology_median_1995-2014.nc`;
}

/** Referenz der Quelle direkt lesbar (NetCDF-3) */
export const CCKP_HISTORICAL_NC3 = new Set(['tas']);

export function robustnessOf(v: number): Robustness {
  if (!Number.isFinite(v) || Math.abs(v) >= FILL_LIMIT) return 'robust';
  if (v === 1) return 'keine Änderung';
  if (v === 2) return 'widersprüchlich';
  return 'keine Daten';
}

export interface HeaderCache {
  get(url: string): Promise<{ header: Nc3Header; etag: string | null } | undefined>;
  put(url: string, value: { header: Nc3Header; etag: string | null }): Promise<void>;
}

export const memoryHeaderCache = (): HeaderCache => {
  const m = new Map<string, { header: Nc3Header; etag: string | null }>();
  return {
    get: async (u) => m.get(u),
    put: async (u, v) => void m.set(u, v),
  };
};

export interface CckpContext {
  fetcher?: RangeFetcher;
  headers?: HeaderCache;
  concurrency?: number;
  onProgress?: (done: number, total: number) => void;
  now?: () => Date;
  /** Teilabruf fürs Portfolio: nur diese Kennzahlen bzw. Auswahlen */
  indicators?: FutureIndicator[];
  selections?: string[];
}

interface Grid {
  lat: number[];
  lon: number[];
}

function nearestIndex(axis: number[], x: number): number {
  let best = 0;
  for (let k = 1; k < axis.length; k++) if (Math.abs(axis[k] - x) < Math.abs(axis[best] - x)) best = k;
  return best;
}

async function pool<T, R>(items: T[], n: number, fn: (x: T) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(n, items.length) }, async () => {
      while (next < items.length) {
        const k = next++;
        out[k] = await fn(items[k]);
      }
    }),
  );
  return out;
}

export interface CckpCell {
  i: number;
  j: number;
  lat: number;
  lon: number;
  distanceKm: number;
  /** Ersatzzelle, weil die nächste Zelle keinen Wert hat (z. B. Meer) */
  substituted: boolean;
}

export interface CckpOutcome {
  cell: CckpCell;
  rows: FutureRow[];
  files: FutureFileRecord[];
  notes: string[];
}

/**
 * Liest alle Kennzahlen für einen Ort. Wirft bei Ausfall der Quelle; es gibt
 * keinen stillen Ersatz.
 */
export async function loadCckp(lat: number, lon: number, ctx: CckpContext = {}): Promise<CckpOutcome> {
  const fetcher = ctx.fetcher ?? fetchRange;
  const cache = ctx.headers ?? memoryHeaderCache();
  const now = ctx.now ?? (() => new Date());
  const files = new Map<string, FutureFileRecord>();
  const models = new Map<string, number>();
  const notes: string[] = [];

  const inflight = new Map<string, Promise<{ header: Nc3Header; etag: string | null }>>();
  const header = (url: string) => {
    let p = inflight.get(url);
    if (!p) {
      p = (async () => {
        let h = await cache.get(url);
        if (!h) {
          h = await readNc3Header(url, fetcher);
          await cache.put(url, h);
          files.set(url, { url, bytes: h.header.headerLength, etag: h.etag, retrievedAt: now().toISOString() });
        }
        for (const x of h.header.vars.values()) {
          const m = x.attrs.models_in_ensemble;
          if (typeof m === 'string') models.set(url, m.split(',').filter((s) => s.trim()).length);
        }
        return h;
      })();
      inflight.set(url, p);
      p.catch(() => inflight.delete(url));
    }
    return p;
  };
  const value = async (url: string, varName: string, i: number, j: number): Promise<number> => {
    let h = await header(url);
    let r = await readNc3Value(url, h.header, varName, [0, i, j], fetcher);
    if (h.etag && r.etag && h.etag !== r.etag) {
      // Datei wurde an der Quelle ersetzt: Dateikopf neu lesen
      const fresh = await readNc3Header(url, fetcher);
      await cache.put(url, fresh);
      inflight.set(url, Promise.resolve(fresh));
      h = fresh;
      r = await readNc3Value(url, h.header, varName, [0, i, j], fetcher);
      notes.push(`Quelldatei seit letztem Abruf geändert: ${url.split('/').pop()}`);
    }
    const rec = files.get(url) ?? { url, bytes: 0, etag: r.etag, retrievedAt: now().toISOString() };
    rec.bytes += 4;
    rec.etag = r.etag ?? rec.etag;
    files.set(url, rec);
    return r.value;
  };

  // Raster und Zelle bestimmen: nächste Zelle mit Wert, sonst Nachbarzellen bis 2 Zellen Abstand
  const probeSel = CCKP_SELECTIONS[1];
  const probeUrl = anomalyUrl('tas', probeSel, 'median');
  const ph = await header(probeUrl);
  const grid: Grid = { lat: await readNc3Axis(probeUrl, ph.header, 'lat', fetcher), lon: await readNc3Axis(probeUrl, ph.header, 'lon', fetcher) };
  // Längenachse −180…180 (lonFlip) oder 0…360
  const lonN = Math.max(...grid.lon) > 180 ? ((lon % 360) + 360) % 360 : ((((lon + 180) % 360) + 360) % 360) - 180;
  const i0 = nearestIndex(grid.lat, lat);
  const j0 = nearestIndex(grid.lon, lonN);
  const candidates: { i: number; j: number; d: number }[] = [];
  for (let di = -2; di <= 2; di++)
    for (let dj = -2; dj <= 2; dj++) {
      const i = i0 + di;
      const j = (j0 + dj + grid.lon.length) % grid.lon.length;
      if (i < 0 || i >= grid.lat.length) continue;
      candidates.push({ i, j, d: distanceKm(lat, lonN, grid.lat[i], grid.lon[j]) });
    }
  candidates.sort((a, b) => a.d - b.d);
  let cell: CckpCell | null = null;
  for (const c of candidates) {
    const v = await value(probeUrl, 'anomaly-tas-annual-mean', c.i, c.j);
    if (Number.isFinite(v) && Math.abs(v) < FILL_LIMIT) {
      cell = { i: c.i, j: c.j, lat: grid.lat[c.i], lon: grid.lon[c.j], distanceKm: c.d, substituted: c !== candidates[0] };
      break;
    }
  }
  if (!cell) throw new Error('Keine Weltbank-Zelle mit Werten in der Nähe (Ort auf dem Meer oder ausserhalb der Landfläche)');
  if (cell.substituted) notes.push(`Nächste 0.25°-Zelle ohne Wert; benachbarte Landzelle in ${cell.distanceKm.toFixed(0)} km verwendet.`);

  // Alle Einzelwerte parallel lesen
  const indicators = ctx.indicators ? CCKP_INDICATORS.filter((x) => ctx.indicators!.includes(x.indicator)) : CCKP_INDICATORS;
  const selections = ctx.selections ? CCKP_SELECTIONS.filter((x) => ctx.selections!.includes(x.key)) : CCKP_SELECTIONS;
  const historical = [...CCKP_HISTORICAL_NC3].filter((code) => indicators.some((x) => x.code === code));
  type Job = { ind: CckpIndicator; sel: CckpSelection; stat: CckpStat | 'sig' };
  const jobs: Job[] = [];
  for (const ind of indicators)
    for (const sel of selections) {
      for (const stat of CCKP_STATS) jobs.push({ ind, sel, stat });
      jobs.push({ ind, sel, stat: 'sig' });
    }
  let done = 0;
  const total = jobs.length + historical.length;
  const results = await pool(jobs, ctx.concurrency ?? 12, async (job) => {
    const url = anomalyUrl(job.ind.code, job.sel, job.stat === 'sig' ? 'median' : job.stat);
    const varName = `${job.stat === 'sig' ? 'anomalysignificance' : 'anomaly'}-${job.ind.code}-annual-mean`;
    const v = await value(url, varName, cell.i, cell.j);
    ctx.onProgress?.(++done, total);
    return v;
  });
  const hist = new Map<string, number>();
  for (const code of historical) {
    const url = historicalUrl(code);
    const v = await value(url, `climatology-${code}-annual-mean`, cell.i, cell.j);
    if (Math.abs(v) < FILL_LIMIT) hist.set(code, v);
    ctx.onProgress?.(++done, total);
  }

  const rows: FutureRow[] = indicators.map((ind) => {
    const deltas: Record<string, Spread | null> = {};
    const robustness: Record<string, Robustness | null> = {};
    for (const sel of selections) {
      const get = (stat: Job['stat']) => results[jobs.findIndex((x) => x.ind === ind && x.sel === sel && x.stat === stat)];
      const [m, lo, hi] = [get('median'), get('p10'), get('p90')];
      const ok = [m, lo, hi].every((x) => Number.isFinite(x) && Math.abs(x) < FILL_LIMIT);
      const n = models.get(anomalyUrl(ind.code, sel, 'median')) ?? 0;
      // P10 ≤ P90 auch bei negativem Faktor
      const [a, b] = [lo * ind.factor, hi * ind.factor].sort((x, y) => x - y);
      deltas[sel.key] = ok ? { p10: a, p50: m * ind.factor, p90: b, n } : null;
      robustness[sel.key] = ok ? robustnessOf(get('sig')) : null;
    }
    const ref = hist.get(ind.code);
    return { indicator: ind.indicator, definition: ind.definition, unit: ind.unit, sourceRef: ref === undefined ? null : ref * ind.factor, deltas, robustness };
  });
  return { cell, rows, files: [...files.values()], notes };
}

